import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const ADMIN_COLLEGES_API = process.env.REACT_APP_ADMIN_COLLEGES_API || `${API_BASE_URL}/api/admin-app/college-profiles/`;

const AdminCollegeEntryPage = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({
        college_name: '',
        college_place: '',
        college_id: '',
        password: '',
    });
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isFormOpen, setIsFormOpen] = useState(false);

    const headers = useMemo(() => {
        const token = (localStorage.getItem('guest_access_token') || sessionStorage.getItem('guest_access_token') || '').trim();
        if (!token) {
            return null;
        }

        return {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        };
    }, []);

    const detailUrl = (id) => `${ADMIN_COLLEGES_API}${id}/`;

    const resetForm = () => {
        setForm({ college_name: '', college_place: '', college_id: '', password: '' });
        setEditingId(null);
        setIsFormOpen(false);
    };

    const openAddCollegeForm = () => {
        setEditingId(null);
        setForm({ college_name: '', college_place: '', college_id: '', password: '' });
        setErrorMessage('');
        setSuccessMessage('');
        setIsFormOpen(true);
    };

    const fetchRows = async () => {
        if (!headers) {
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(ADMIN_COLLEGES_API, { headers });
            const data = response.ok ? await response.json() : [];
            setRows(Array.isArray(data) ? data : []);
        } catch (error) {
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRows();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [headers]);

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        if (errorMessage) {
            setErrorMessage('');
        }
        if (successMessage) {
            setSuccessMessage('');
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setErrorMessage('');
        setSuccessMessage('');

        if (!headers) {
            setErrorMessage('Session expired. Please log in again.');
            return;
        }

        const collegeName = form.college_name.trim();
        const collegePlace = form.college_place.trim();
        const collegeCode = form.college_id.trim();
        const password = form.password.trim();

        if (!collegeName || !collegeCode) {
            setErrorMessage('College name and college code are required.');
            return;
        }

        if (!editingId && password.length < 6) {
            setErrorMessage('Password is required and must be at least 6 characters for new college.');
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                college_name: collegeName,
                college_place: collegePlace,
                college_id: collegeCode,
            };

            if (password) {
                payload.password = password;
            }

            const response = await fetch(editingId ? detailUrl(editingId) : ADMIN_COLLEGES_API, {
                method: editingId ? 'PATCH' : 'POST',
                headers,
                body: JSON.stringify(payload),
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                const firstError = data?.error
                    || data?.college_name?.[0]
                    || data?.college_id?.[0]
                    || data?.password?.[0]
                    || 'Unable to save college entry.';
                setErrorMessage(firstError);
                return;
            }

            setSuccessMessage(editingId ? 'College updated successfully.' : 'New college added successfully.');
            resetForm();
            await fetchRows();
        } catch (error) {
            setErrorMessage('Unable to reach server right now.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        setForm({
            college_name: item.college_name || '',
            college_place: item.college_place || '',
            college_id: item.college_id || '',
            password: '',
        });
        setErrorMessage('');
        setSuccessMessage('Editing selected college. Leave password empty to keep current password.');
        setIsFormOpen(true);
    };

    const handleDelete = async (itemId) => {
        if (!headers) {
            setErrorMessage('Session expired. Please log in again.');
            return;
        }

        const result = await Swal.fire({
            title: 'Delete college?',
            text: 'This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel',
        });
        if (!result.isConfirmed) return;

        setSubmitting(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const response = await fetch(detailUrl(itemId), {
                method: 'DELETE',
                headers,
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                setErrorMessage(data?.error || 'Unable to delete college.');
                return;
            }

            if (editingId === itemId) {
                resetForm();
            }

            setSuccessMessage('College deleted successfully.');
            await fetchRows();
        } catch (error) {
            setErrorMessage('Unable to reach server right now.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="admin-panel-card admin-table-section">
            {isFormOpen ? (
                <div className="admin-top-sheet-backdrop" onClick={resetForm}>
                    <form
                        className="admin-college-form admin-top-sheet"
                        onSubmit={handleSubmit}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="admin-top-sheet-header">
                            <h4 className="admin-college-dropdown-title">{editingId ? 'Edit College' : 'Add New College'}</h4>
                            <button
                                type="button"
                                className="admin-top-sheet-close"
                                onClick={resetForm}
                                disabled={submitting}
                                aria-label="Close add college form"
                            >
                                x
                            </button>
                        </div>

                        <div className="admin-college-form-grid">
                            <div>
                                <label className="admin-security-label" htmlFor="college_name">College Name</label>
                                <input
                                    id="college_name"
                                    name="college_name"
                                    type="text"
                                    className="admin-security-input"
                                    value={form.college_name}
                                    onChange={handleInputChange}
                                    disabled={submitting}
                                />
                            </div>
                            <div>
                                <label className="admin-security-label" htmlFor="college_place">College Place</label>
                                <input
                                    id="college_place"
                                    name="college_place"
                                    type="text"
                                    className="admin-security-input"
                                    value={form.college_place}
                                    onChange={handleInputChange}
                                    disabled={submitting}
                                />
                            </div>
                            <div>
                                <label className="admin-security-label" htmlFor="college_id">College Code</label>
                                <input
                                    id="college_id"
                                    name="college_id"
                                    type="text"
                                    className="admin-security-input"
                                    value={form.college_id}
                                    onChange={handleInputChange}
                                    disabled={submitting}
                                />
                            </div>
                            <div>
                                <label className="admin-security-label" htmlFor="password">
                                    {editingId ? 'New Password (Optional)' : 'Password'}
                                </label>
                                <input
                                    id="password"
                                    name="password"
                                    type="password"
                                    className="admin-security-input"
                                    value={form.password}
                                    onChange={handleInputChange}
                                    disabled={submitting}
                                />
                            </div>
                        </div>

                        <div className="admin-college-actions">
                            <button type="submit" className="admin-profile-action-btn admin-profile-action-btn-primary admin-college-primary-btn" disabled={submitting}>
                                {submitting ? 'Saving...' : editingId ? 'Update College' : 'Add College'}
                            </button>
                            <button
                                type="button"
                                className="admin-profile-action-btn"
                                onClick={resetForm}
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            ) : null}

            <div className="admin-college-header">
                <div>
                    <h3>College Entry</h3>
                    <p className="admin-subtitle">Manage colleges with add, edit, and delete options.</p>
                </div>
                <button
                    type="button"
                    className="admin-profile-action-btn admin-profile-action-btn-primary admin-college-primary-btn"
                    onClick={openAddCollegeForm}
                    disabled={submitting}
                >
                    Add College
                </button>
            </div>

            {errorMessage ? <div className="admin-security-feedback admin-security-feedback-error">{errorMessage}</div> : null}
            {successMessage ? <div className="admin-security-feedback admin-security-feedback-success">{successMessage}</div> : null}

            <div className="admin-table-wrap">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>College Name</th>
                            <th>College Place</th>
                            <th>College Code</th>
                            <th>Updated</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="6">Loading colleges...</td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan="6">No colleges found.</td></tr>
                        ) : (
                            rows.map((item) => (
                                <tr key={item.id}>
                                    <td>{item.id}</td>
                                    <td>{item.college_name || '-'}</td>
                                    <td>{item.college_place || '-'}</td>
                                    <td>{item.college_id || '-'}</td>
                                    <td>{item.updated_at ? new Date(item.updated_at).toLocaleString() : '-'}</td>
                                    <td>
                                        <div className="admin-row-actions">
                                            <button
                                                type="button"
                                                className="admin-row-action-btn"
                                                onClick={() => handleEdit(item)}
                                                disabled={submitting}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                className="admin-row-action-btn admin-row-action-btn-danger"
                                                onClick={() => handleDelete(item.id)}
                                                disabled={submitting}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminCollegeEntryPage;
